import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { State } from '../../typings/state';
import {
    VideoCreatorImageData,
    VideoCreatorResourceType,
    VideoCreatorTemplate,
    VideoCreatorTemplateScene
} from '../VideoCreatorHelper/typings';

import { VideoCreatorApiResponse, VideoCreatorApiVideoData, VideoCreatorVideoResources } from './typings';

const VIDEO_CREATOR_DEFAULT_VIDEO_SCALE = 3;
const VIDEO_CREATOR_DEFAULT_VIDEO_FPS = 60;

export function mergeTemplateWithVideoResources(
    template: VideoCreatorTemplate,
    videoResources: VideoCreatorVideoResources,
    params?: {}
): VideoCreatorTemplate {
    const mergedTemplate = Object.assign({ ...params }, template);

    if (videoResources.duration && videoResources.duration.resourceType === VideoCreatorResourceType.duration) {
        mergedTemplate.duration.current = videoResources.duration.duration;
    }

    mergedTemplate.scenes = template.scenes.map(
        (scene: VideoCreatorTemplateScene): VideoCreatorTemplateScene => {
            const resourceId = scene.resourceId;
            const videoResource = resourceId && videoResources[resourceId];

            if (resourceId && videoResource) {
                if (videoResource.resourceType === VideoCreatorResourceType.text) {
                    scene.text = videoResource.text;
                    scene.initialState.color = videoResource.color;
                    scene.initialState.fontFamily = videoResource.fontFamily;
                }

                if (videoResource.resourceType === VideoCreatorResourceType.overlay) {
                    scene.initialState.backgroundColor = videoResource.backgroundColor;
                }

                if (videoResource.resourceType === VideoCreatorResourceType.image) {
                    scene.initialState.backgroundImage = `url(${videoResource.backgroundImage})`;
                }

                return scene;
            } else {
                return scene;
            }
        }
    );

    return mergedTemplate;
}

export function getVideoResourcesFromTemplate(template: VideoCreatorTemplate): VideoCreatorVideoResources {
    return template.scenes.reduce(
        (acc, item) => {
            const resourceId = item.resourceId;

            if (resourceId) {
                acc[resourceId] = {
                    resourceType: item.resourceType
                };

                switch (item.resourceType) {
                    case VideoCreatorResourceType.text:
                        acc[resourceId].text = item.text;
                        acc[resourceId].color = item.initialState.color;
                        acc[resourceId].fontFamily = item.initialState.fontFamily;
                        break;
                    case VideoCreatorResourceType.image:
                        acc[resourceId].backgroundImage = getUrlFromBackgroundImageVideoResource(
                            item.initialState.backgroundImage
                        );
                        break;
                    case VideoCreatorResourceType.overlay:
                        acc[resourceId].backgroundColor = item.initialState.backgroundColor;
                        break;
                    default:
                        break;
                }
            }

            return acc;
        },
        {
            duration: {
                resourceType: VideoCreatorResourceType.duration,
                duration: template.duration.current
            }
        }
    );
}

export function getUrlFromBackgroundImageVideoResource(backgroundImageCssProp?: string): string {
    return backgroundImageCssProp ? backgroundImageCssProp.slice(4, -1).replace(/"/g, '') : '';
}

export function getVideoResourcesFromApiResponse(response: VideoCreatorApiResponse): VideoCreatorVideoResources {
    const params = response.params;

    return Object.keys(params).reduce((acc, item) => {
        const param = params[item];

        if (typeof param === 'object' && 'resourceType' in param) {
            acc[item] = param;
        }

        return acc;
    }, {});
}

export function getApiVideoDataForVideoSave(state: State): VideoCreatorApiVideoData {
    const { videoCreatorEdit } = state;
    const editorData = selectEditPageItemNameEditorValue(state.editPageItemNameEditor, videoCreatorEdit.id);
    let name: string = videoCreatorEdit.name;

    if (editorData.isVisible) {
        name = editorData.value;
    }

    return {
        name: name,
        width: Number(videoCreatorEdit.template.width),
        height: Number(videoCreatorEdit.template.height),
        scale: VIDEO_CREATOR_DEFAULT_VIDEO_SCALE,
        fps: VIDEO_CREATOR_DEFAULT_VIDEO_FPS,
        url: `${window.location.origin}/vc-preview`,
        params: {
            templateId: videoCreatorEdit.template.id || '',
            ...getVideoResourcesFromTemplate(videoCreatorEdit.template)
        }
    };
}

export function getImageFromVideoResources(videoResources: VideoCreatorVideoResources): VideoCreatorImageData {
    return Object.keys(videoResources).reduce(
        (acc: VideoCreatorImageData, id: string) => {
            const resource = videoResources[id];

            if (typeof resource === 'object' && 'resourceType' in resource && resource.resourceType === 'image') {
                acc.url = resource.backgroundImage;
                acc.id = id;

                return acc;
            }

            return acc;
        },
        { id: '', url: '' }
    );
}
